import { memo, useCallback, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { SxProps, Theme } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";

import CrmMessageBatchHistoryDialog from "./CrmMessageBatchHistoryDialog";
import CrmMessageBatchHistoryPanel from "./CrmMessageBatchHistoryPanel";
import { useCrmMessageFranchiseStats } from "../hooks/useCrmMessageFranchiseStats";

export interface CrmMessageFranchiseUsagePanelProps {
  /**
   * ID da organização (CNPJ / tenant) — necessário para buscar Contract, Task e PaymentNotice.
   * Sem valor, o painel não dispara chamadas FHIR.
   */
  organizationId?: string;
  /**
   * Incrementar após fluxos como pagamento de pacote para recarregar totais (ex.: +1 a cada conclusão).
   */
  statsRefreshSignal?: number;
  /** Espaçamento extra no topo (ex.: após botões). */
  sx?: SxProps<Theme>;
}

/**
 * Uso de mensagens vs franquia: disponíveis, enviadas e total contratado (mesma regra do FluxZap / `CRMQuickStats`).
 */
const CrmMessageFranchiseUsagePanel = memo(
  ({
    organizationId,
    statsRefreshSignal = 0,
    sx: sxProp,
  }: CrmMessageFranchiseUsagePanelProps) => {
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

    const { stats, batchHistory, isLoading, loadError, refetch } =
      useCrmMessageFranchiseStats(organizationId, statsRefreshSignal);

    const handleRefreshClick = useCallback(() => {
      void refetch();
    }, [refetch]);
    const openHistoryModal = useCallback(() => {
      setIsHistoryModalOpen(true);
    }, []);
    const closeHistoryModal = useCallback(() => {
      setIsHistoryModalOpen(false);
    }, []);

    const utilizationRounded = useMemo(
      () => Math.round(stats.usageRateInPercentage),
      [stats.usageRateInPercentage],
    );
    const summarizedBatchHistory = useMemo(
      () => batchHistory.slice(0, 3),
      [batchHistory],
    );

    const sxArray =
      sxProp != null ? (Array.isArray(sxProp) ? sxProp : [sxProp]) : [];

    const hasOrganization = Boolean(organizationId?.trim());

    return (
      <>
        <Paper
          variant="outlined"
          sx={[{ p: 1.5, borderRadius: 2 }, ...sxArray]}
        >
          <Stack spacing={1.5}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <SmsOutlinedIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" fontWeight={700}>
                  Estatísticas de mensagens e franquia
                </Typography>
              </Stack>
              {hasOrganization ? (
                <Tooltip title="Atualizar estatísticas">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Atualizar estatísticas"
                      onClick={handleRefreshClick}
                      disabled={isLoading}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
            </Stack>

            {!hasOrganization ? (
              <Typography variant="body2" color="text.secondary">
                Informe o identificador da organização para carregar as
                estatísticas de mensagens.
              </Typography>
            ) : null}

            {loadError != null ? (
              <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
                Não foi possível carregar todas as métricas. Use o botão de
                atualizar para tentar novamente.
              </Alert>
            ) : null}

            {isLoading && hasOrganization ? (
              <Stack spacing={1}>
                <Skeleton variant="rounded" height={20} />
                <Skeleton variant="rounded" height={10} />
                <Skeleton variant="text" width="40%" />
              </Stack>
            ) : null}

            {!isLoading && hasOrganization ? (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                    gap: 1,
                  }}
                >
                  <Stack spacing={0.25}>
                    <Typography variant="caption" color="text.secondary">
                      Disponíveis
                    </Typography>
                    <Typography variant="h6" component="p" fontWeight={700}>
                      {stats.remainingMessagesForSend}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Saldo restante para envio (contrato CRM)
                    </Typography>
                  </Stack>
                  <Stack spacing={0.25}>
                    <Typography variant="caption" color="text.secondary">
                      Enviadas
                    </Typography>
                    <Typography variant="h6" component="p" fontWeight={700}>
                      {stats.totalMessagesUsed}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Mensagens já enviadas
                    </Typography>
                  </Stack>
                  <Stack spacing={0.25}>
                    <Typography variant="caption" color="text.secondary">
                      Total contratado
                    </Typography>
                    <Typography variant="h6" component="p" fontWeight={700}>
                      {stats.totalMessagesContracted}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total de mensagens contratadas
                    </Typography>
                  </Stack>
                </Box>

                <LinearProgress
                  variant="determinate"
                  value={Math.min(
                    100,
                    Math.max(0, stats.usageRateInPercentage),
                  )}
                  sx={{ borderRadius: 999, height: 10 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Utilização (enviadas ÷ disponíveis): {utilizationRounded}%
                </Typography>
              </>
            ) : null}
          </Stack>
        </Paper>
        <CrmMessageBatchHistoryDialog
          open={isHistoryModalOpen}
          onClose={closeHistoryModal}
          batchHistory={batchHistory}
        />
      </>
    );
  },
);

CrmMessageFranchiseUsagePanel.displayName = "CrmMessageFranchiseUsagePanel";

export default CrmMessageFranchiseUsagePanel;
